/* eslint-disable no-sequences */
/* eslint-disable no-return-assign */
import React, { useEffect, useRef } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useIntl } from 'react-intl';
import { BLOCKS } from '@contentful/rich-text-types';
import { useSelector } from 'react-redux';
import {
  cartStoreSelector,
  cartItemsSelector,
} from '../../../redux/selectors/cartSelectors';
import ColourPicker from '../../elements/ColourPicker';
import MemorySelector from '../../elements/MemorySelector';
import { StoreList } from '../../StoreLocator';
import Phobio from '../../Phobio/Phobio';
import RichText from '../../UI/RichText';
import ConflictingStoreMessage from '../../CartComponents/ConflictingStoreMessage/ConflictingStoreMessage';
import { ItemType, ReserveStatus } from '../../../types';
import useGetTradeInPhones from '../../../hooks/useGetTradeInPhones';
import { phobioSelector } from '../../../redux/selectors/phoneSelectors';
import { isKiosk } from '../../../utils/isKiosk';
import { AllContentfulPhoneBrand } from '../../../ContentfulTypes';
import CarrierOffers from './CarrierOffers';
import ReserveForm from './ReserveForm/ReserveForm';
import {
  Phase1Screen,
  BladeTitle,
  BladeSubtitle,
  SectionHeading,
  SectionWrapper,
  PhoneTitle,
  ColourPickerContainer,
  MemoryContainer,
  CarriersList,
  LegalText,
  ErrorMsg,
  NextButton,
  StoreSearch,
  UIWrapper,
} from './styles';

interface Props {
  variantLabels: Record<string, any>;
  reserveData: Record<string, any>;
  phone: Record<string, any>;
  colours: Record<string, any>[];
  selectedColour: string;
  onColourChange: (...args: any[]) => any;
  storageOptions: string[];
  selectedStorageSize: string;
  selectSize: (...args: any[]) => any;
  personalDetails: Record<string, any>;
  hasErrorPersonalDetails: boolean;
  deliverFormField: (...args: any[]) => any;
  availableOffers: Record<string, any>[];
  selectedCarrier?: string;
  handleCarrierChange: (...args: any[]) => any;
  hasErrorCarrier: boolean;
  selectLocation: (...args: any[]) => any;
  selectedLocation?: Record<string, any>;
  hasErrorLocation: boolean;
  handlePhase2: (...args: any[]) => any;
  nearbyStores?: Record<string, any>[];
  allStoreLogos: Record<string, any>[];
  setInputRef: (...args: any[]) => any;
  inventoryLabels?: Record<string, any>;
  page?: string;
  checkErrorTrigger: boolean;
  selectedSKU?: string;
  setSelectedSKU: (...args: any[]) => any;
  reserveStatus?: string;
  showTradeIn?: boolean;
  showDisclaimer?: boolean;
  phoneBrands: AllContentfulPhoneBrand;
  isReserveAccessoriesFFEnabled: boolean;
}

// Elements to render on rich text
const legalOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <LegalText>{children}</LegalText>,
  },
};

const ReservePhase1 = ({
  reserveData,
  phone,
  colours,
  selectedColour,
  onColourChange,
  storageOptions,
  selectedStorageSize,
  selectSize,
  personalDetails,
  hasErrorPersonalDetails,
  deliverFormField,
  variantLabels,
  availableOffers,
  selectedCarrier,
  handleCarrierChange,
  hasErrorCarrier,
  selectLocation,
  selectedLocation,
  hasErrorLocation,
  handlePhase2,
  nearbyStores,
  allStoreLogos,
  setInputRef,
  inventoryLabels,
  page,
  checkErrorTrigger = true,
  selectedSKU,
  setSelectedSKU,
  reserveStatus = ReserveStatus.ChatWithMobilist,
  showTradeIn = true,
  showDisclaimer = true,
  phoneBrands,
  isReserveAccessoriesFFEnabled,
}: Props) => {
  const { locale } = useIntl();
  const locationErrorRef = useRef();
  const carrierErrorRef = useRef();
  const cartStoreData = useSelector(cartStoreSelector);
  const cartData = useSelector(cartItemsSelector);

  const cartItems = useSelector(cartItemsSelector);
  const selectedTradeInDevice = useSelector(phobioSelector);

  const tradeInApplied = cartItems.some((item) => item.itemType === ItemType.Phone)
    && selectedTradeInDevice;
  // Reserve labels data
  const reserveLabelSection = reserveData.combinatorSectionItems.find(
    (item) => item.contentful_id === '7AwlqHWaM2ya9w144ucM6q',
  );

  const reserveLabels = reserveLabelSection.formFields.reduce(
    (obj, item) => ((obj[item.contentful_id] = item), obj),
    {},
  );
  // Legal section 1
  const legalSection1 = reserveData.combinatorSectionItems.find((item) => item.contentful_id === '6V4YaXMf40oxjCWZS06MsO');
  // Legal section 2
  const legalSection2 = reserveData.combinatorSectionItems.find((item) => item.contentful_id === '2Igj9xJNqQVD1argTrF1uo');

  // Phobio data
  const phobioData = reserveData.combinatorSectionItems.find((item) => item.contentful_id === '5WUtBHJGymhReE1Tai5Plq');

  const { response: tradeInPhones, loading: tradeInLoading } = useGetTradeInPhones(
    { minTradeInValue: 1 },
  );
  // CREATE DATALAYER FOR GTM ANALYTICS
  useEffect(() => {
    // Create dataLayer object for GTM if it doesn't exist yet
    window.dataLayer = window.dataLayer || [];
    // Structure data to submit
    const phase1Data = {
      event: 'reservenow',
      reservePhase: 'phase 1 - order form',
      phoneSummary: `${phone.displayName} ${selectedStorageSize} ${selectedColour}`,
      phoneModel: phone.model,
      phoneColour: selectedColour,
      phoneStorage: `${selectedStorageSize}`,
      carrier: null,
      storeSummary: null,
    };
    window.dataLayer.push(phase1Data);

    /* eslint-disable */
    //check if hj function exists
    (window as any).hj =
      (window as any).hj ||
      function () {
        (hj.q = hj.q || []).push(arguments);
      };
    hj('trigger', 'phone_reservation');
    /* eslint-enable */
  }, [phone.displayName, phone.model, selectedColour, selectedStorageSize]);

  // CHANGE HANDLER FOR CARRIER RADIO INPUTS
  // Div click event to trigger radio click
  const triggerRadio = (offer) => {
    // Get value of chosen carrier
    const chosenCarrier = document.getElementById(offer.phoneCarrier.cmsName);
    setSelectedSKU(offer.sku);
    const chosenCarrierImg = offer.phoneCarrier.logo;
    const chosenCarrierUpfrontCost = offer.carrierUpfrontPrice;
    const chosenCarrierMonthlyCost = offer.monthlyPrice;
    const chosenCarrierPcoPoints = offer.pcoPoints;
    // Remove red border from any existing carriers, and add it to newly chosen carrier
    const allCarrierItems = document.getElementsByClassName('carrierListItem');

    for (let x = 0; x < allCarrierItems.length; x += 1) {
      allCarrierItems[x].classList.remove('chosenCarrier');
    }
    chosenCarrier?.classList.add('chosenCarrier');
    // Lift chosen carrier value up
    handleCarrierChange(
      offer.phoneCarrier.cmsName,
      chosenCarrierImg,
      chosenCarrierUpfrontCost,
      chosenCarrierMonthlyCost,
      chosenCarrierPcoPoints,
      offer,
    );
  };

  // This section will remove any previously selected carrier if phone memory/colour selection is changed
  useEffect(() => {
    const allCarrierItems = document.getElementsByClassName('carrierListItem');

    for (let x = 0; x < allCarrierItems.length; x += 1) {
      allCarrierItems[x].classList.remove('chosenCarrier');
    }
    setSelectedSKU(null);
  }, [selectedColour, selectedStorageSize, availableOffers, setSelectedSKU]);

  // This section will scroll the screen to earliest error on the blade
  useEffect(() => {
    if (hasErrorLocation) {
      locationErrorRef?.current?.focus();
      locationErrorRef?.current?.scrollIntoViewIfNeeded(true);
    }
    if (hasErrorCarrier) {
      carrierErrorRef?.current?.focus();
      if (carrierErrorRef?.current?.scrollIntoViewIfNeeded) {
        carrierErrorRef?.current?.scrollIntoViewIfNeeded(true);
      }
    }
  }, [checkErrorTrigger, hasErrorCarrier, hasErrorLocation]);

  return (
    <Phase1Screen>
      <BladeTitle data-testid="blade-title">
        {ReactHtmlParser(reserveLabels['311rbxJLT8pMl6csMagL0b'].displayLabel)}
      </BladeTitle>

      {/* PHONE DETAILS */}
      <SectionWrapper>
        <BladeSubtitle>
          {ReactHtmlParser(
            reserveLabels['7rG36p3ep1ewgsgqBT9QFX'].displayLabel,
          )}
        </BladeSubtitle>
        <PhoneTitle data-testid="phone-detail-title">
          {phone.displayName}
          {' '}
          {selectedStorageSize}
          {' '}
          {selectedColour}
        </PhoneTitle>
        <UIWrapper>
          <ColourPickerContainer>
            <SectionHeading>
              {ReactHtmlParser(
                reserveLabels['4auM3OxXjstv6qdqUWuKPJ'].displayLabel,
              )}
            </SectionHeading>
            <ColourPicker
              onColourChange={onColourChange}
              colours={colours}
              selectedColour={selectedColour}
              colourSelectable
            />
          </ColourPickerContainer>
          <MemoryContainer>
            <SectionHeading>
              {ReactHtmlParser(
                reserveLabels.SAz2sQ260Eomsbi4WjtG2.displayLabel,
              )}
            </SectionHeading>
            <MemorySelector
              className="memorySelector"
              availableSizes={storageOptions}
              selectedStorageSize={selectedStorageSize}
              selectSize={selectSize}
            />
          </MemoryContainer>
        </UIWrapper>
      </SectionWrapper>
      <hr />

      {/* CARRIERS */}
      <SectionWrapper id="carriersWrapper">
        {/* <SectionWrapper id="carriersWrapper" ref={carrierError}> */}
        <BladeSubtitle>
          {ReactHtmlParser(
            reserveLabels['2ozUfkPbgqPI5XPzcc2R11'].displayLabel,
          )}
        </BladeSubtitle>
        {hasErrorCarrier && (
          <ErrorMsg role="alert" tabIndex="-1" ref={carrierErrorRef}>
            &#x2718;
            {' '}
            {ReactHtmlParser(
              reserveLabels['2ozUfkPbgqPI5XPzcc2R11'].errorMessage,
            )}
          </ErrorMsg>
        )}
        <CarriersList>
          <CarrierOffers
            availableOffers={availableOffers}
            locale={locale}
            clickHandler={triggerRadio}
            variantLabels={variantLabels}
            selectedCarrier={selectedCarrier}
            ariaLabel={reserveLabels['2ozUfkPbgqPI5XPzcc2R11'].displayLabel}
            reserveStatus={reserveStatus}
          />
        </CarriersList>
        {/* LEGAL TEXT */}
        {showDisclaimer && (
          <RichText
            document={legalSection2.contentBody}
            options={legalOptions}
          />
        )}
      </SectionWrapper>
      <hr />

      {showTradeIn && !tradeInApplied && (
        <>
          <SectionWrapper>
            <Phobio
              phobioData={{ ...phobioData, phoneBrands }}
              locale={locale}
              reservationBlade
              tradeInPhones={tradeInPhones}
              tradeInLoading={tradeInLoading}
            />
          </SectionWrapper>
          <hr />
        </>
      )}

      {/* LOCATIONS */}
      {!isKiosk() && (
        <SectionWrapper>
          {/* <SectionWrapper ref={locationError}> */}

          <BladeSubtitle>
            {ReactHtmlParser(
              reserveLabels['28xF5GUoIpWCyKJfznZjHM'].displayLabel,
            )}
          </BladeSubtitle>
          {hasErrorLocation && (
            <ErrorMsg role="alert" tabIndex="-1" ref={locationErrorRef}>
              &#x2718;
              {' '}
              {ReactHtmlParser(
                reserveLabels.xcxtFgo2mvaki25PAcGWd.errorMessage,
              )}
            </ErrorMsg>
          )}
          <StoreSearch
            type="text"
            id="google-geocode-autocomplete-reserve-a-phone"
            name="google-geocode-autocomplete-reserve-a-phone"
            aria-label={reserveLabels.xcxtFgo2mvaki25PAcGWd.placeHolderLabel}
            placeholder={reserveLabels.xcxtFgo2mvaki25PAcGWd.placeHolderLabel}
            ref={setInputRef}
          />
          <StoreList
            className="reserve-blade"
            activeStore={selectedLocation}
            nearbyStores={nearbyStores}
            allStoreLogos={allStoreLogos}
            onClick={selectLocation}
            limit={5}
            selectedSKU={selectedSKU}
            selectedCarrier={selectedCarrier}
            inventoryLabels={inventoryLabels}
            page={page}
            includeTemporaryClosed={false}
            hideInventoryStatus={
              reserveStatus === ReserveStatus.ChatWithMobilist
            }
            reserveStatus={reserveStatus}
          />
        </SectionWrapper>
      )}

      {/* PERSONAL INFO FORM */}
      {isReserveAccessoriesFFEnabled ? (
        <SectionWrapper>
          {!!cartData.length && cartStoreData.selectedStoreChanged && (
          <ConflictingStoreMessage />
          )}
        </SectionWrapper>
      ) : (
        <SectionWrapper>
          <ReserveForm
            formData={reserveLabels}
            deliverFormField={deliverFormField}
            personalDetails={personalDetails}
            hasErrorPersonalDetails={hasErrorPersonalDetails}
            checkErrorTrigger={checkErrorTrigger}
          />
          {/* LEGAL TEXT */}
          <RichText
            document={legalSection1.contentBody}
            options={legalOptions}
          />
        </SectionWrapper>
      )}

      {/* PROGRESS TO NEXT SCREEN */}
      <SectionWrapper id="nextButton">
        <NextButton
          type="button"
          onClick={handlePhase2}
          data-ga="cta-review details-phones"
        >
          {ReactHtmlParser(reserveLabels.IJUAhSyGVZqR9usY13tCe.displayLabel)}
        </NextButton>
      </SectionWrapper>
    </Phase1Screen>
  );
};

export default ReservePhase1;
